import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { copyToClipboard, scrollToTop } from "@/utils";
import {
  Check,
  Copy,
  ExternalLink,
  Heart,
  Mail,
  MapPin,
  Phone,
  Radio,
  Youtube,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import HomeContactCard from "./HomeContactCard";
import { LogoFull } from "./Logo";

const HomeGarizim = ({
  displayButtons = true,
}: {
  displayButtons?: boolean;
}) => {
  const navigate = useNavigate();
  const [isInteracOpen, setIsInteracOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleNavigate = (path: string) => {
    scrollToTop();
    navigate(path);
  };

  const handleCopyEmail = async () => {
    const ok = await copyToClipboard("egarizim@gmail.com");
    if (ok) {
      setCopied(true);
      toast.success("Adresse Interac copiée : egarizim@gmail.com");
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <section className="relative bg-slate-950 text-white overflow-hidden border-b border-slate-900">
      {/* Ambient Architectural Lighting */}
      <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-sky-600/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(#1e293b_1px,transparent_1px)] [background-size:24px_24px] opacity-25 pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-8 sm:pt-32 sm:pb-16 lg:py-24 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-10 lg:gap-8 items-center">
          {/* Left Column: Visual & Typographic Anchor (Cols 1 to 7) */}
          <div className="lg:col-span-7 text-left space-y-3 sm:space-y-6">
            {/* Sacred / Community Micro-Badge (Hidden on small devices) */}
            <div className="hidden sm:inline-flex items-center gap-2 px-3 py-1 sm:px-3.5 sm:py-1.5 rounded-full bg-slate-900/90 border border-slate-800 text-slate-300 text-[11px] sm:text-xs font-semibold tracking-wider uppercase max-w-full truncate">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0" />
              <span className="truncate">
                Communauté de Foi • Montréal & Rive-Sud
              </span>
            </div>

            {/* Mobile-Only Left-Aligned Prominent Huge Logo - Kept intact as requested */}
            <div className="flex justify-start w-full py-1 sm:py-4 lg:hidden">
              <LogoFull
                className="h-16 xs:h-20 sm:h-36 max-w-[240px] xs:max-w-[320px] sm:max-w-[450px] w-full object-contain"
                style={{ filter: "invert(1)", marginLeft: "-30px" }}
              />
            </div>

            {/* Main Typographic Headline */}
            <div className="space-y-0.5 sm:space-y-1">
              <h1 className="text-2xl xs:text-3xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.08] text-white">
                ÉGLISE DE DIEU <br />
                <span className="text-white">DE GARIZIM</span>
              </h1>
            </div>

            {/* Scripture Anchor in Editorial Serif */}
            <div className="border-l-2 border-amber-400/80 pl-3 sm:pl-4 py-0.5">
              <p className="font-serif italic text-sm sm:text-xl text-slate-200 leading-snug sm:leading-relaxed font-normal">
                « Sur le mont Garizim, bénissez le peuple »
              </p>
              <span className="text-[10px] sm:text-xs font-medium uppercase tracking-widest text-amber-300/90 block mt-0.5">
                Deutéronome 11:29
              </span>
            </div>

            {/* Welcoming Narrative */}
            <p className="text-md sm:text-base lg:text-lg text-slate-300 max-w-xl leading-relaxed">
              Un sanctuaire vivant d'adoration, d'espérance et de fraternité
              chrétienne. Venez célébrer la grâce de Dieu et grandir avec une
              communauté unie.
            </p>

            {/* Primary Action Buttons - Compact 3-col on mobile */}
            {displayButtons && (
              <div className="grid grid-cols-3 sm:flex sm:flex-row items-center gap-2 sm:gap-3 pt-1 sm:pt-2 w-full sm:w-auto">
                <Button
                  size="default"
                  className="w-full sm:w-auto bg-white hover:bg-slate-100 text-slate-950 font-bold px-2 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-base shadow-md shadow-white/10 transition-all hover:scale-[1.02] justify-center h-auto"
                  onClick={() =>
                    window.open(
                      "https://www.facebook.com/eglisegarizim/",
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  <Radio className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-red-600 animate-pulse shrink-0" />
                  <span>En direct</span>
                </Button>

                <Button
                  variant="outline"
                  size="default"
                  className="w-full sm:w-auto border-slate-700 bg-slate-900/60 hover:bg-slate-800 text-slate-200 hover:text-white font-semibold px-2 sm:px-5 py-2 sm:py-3 rounded-full text-xs sm:text-base backdrop-blur transition-all justify-center h-auto"
                  onClick={() => setIsInteracOpen(true)}
                >
                  <Heart className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-amber-400 shrink-0" />
                  <span className="truncate">Don par interac</span>
                </Button>

                <Button
                  variant="outline"
                  size="default"
                  className="w-full sm:w-auto border-slate-800 bg-slate-900/60 hover:bg-slate-800/90 text-slate-300 hover:text-white font-medium px-2 sm:px-5 py-2 sm:py-3 rounded-full text-xs sm:text-base backdrop-blur transition-all justify-center h-auto"
                  onClick={() => handleNavigate("/archives-videos")}
                >
                  <Youtube className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-red-500/80 shrink-0" />
                  <span className="truncate">Vidéos</span>
                </Button>
              </div>
            )}
          </div>

          {/* Right Column: Gathering Hub Card (Cols 8 to 12) */}
          <div className="lg:col-span-5 space-y-4 sm:space-y-5 mt-4">
            <div className="rounded-3xl bg-slate-900/100 border border-slate-800/90 p-4 sm:p-8 shadow-2xl backdrop-blur-xl relative space-y-3.5 sm:space-y-6">
              {/* Header: Service Schedule */}
              <div className="flex items-start justify-between border-b border-slate-800/80 pb-3 sm:pb-5 gap-3">
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] sm:text-[11px] uppercase tracking-widest font-bold text-amber-400 block mb-0.5">
                    Rassemblement Principal
                  </span>
                  <h3 className="text-lg sm:text-2xl font-black text-white tracking-tight">
                    Culte d'Adoration
                  </h3>
                  {/* <p className="text-slate-400 text-xs sm:text-sm mt-0.5">
                    Tous les dimanches matin
                  </p> */}
                </div>
                <div className="text-right bg-slate-800/80 px-2.5 py-1 sm:px-3.5 sm:py-2 rounded-xl border border-slate-700/60 shrink-0">
                  <span className="text-[9px] sm:text-xs text-slate-400 uppercase tracking-wider block font-semibold">
                    Heure
                  </span>
                  <span className="text-sm sm:text-lg font-extrabold text-white">
                    10h00
                  </span>
                </div>
              </div>

              {/* Location Detail */}
              <div className="space-y-2.5 sm:space-y-3">
                <div className="flex items-start gap-3">
                  <div className="flex-1 text-xs sm:text-sm">
                    <Button
                      className="w-full bg-slate-800 hover:bg-slate-700 text-white font-semibold py-2.5 sm:py-3 rounded-xl border border-slate-700 transition-all flex items-center justify-center gap-2 text-xs sm:text-sm"
                      onClick={() =>
                        window.open(
                          "https://maps.app.goo.gl/MtFkssZ8bBjsLcKcA",
                          "_blank",
                        )
                      }
                    >
                      <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-400" />
                      <span>11800 4e Avenue, Mtl, H1E5Y2</span>
                    </Button>
                  </div>
                </div>

                {/* Direct Contact */}
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-800/60 text-xs">
                  <a
                    href="tel:514-322-4921"
                    className="flex items-center justify-center sm:justify-start gap-1.5 p-2 sm:p-2.5 rounded-xl bg-slate-950/60 hover:bg-slate-800/60 border border-slate-800/80 text-slate-300 transition-colors"
                  >
                    <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="font-mono truncate">(514) 322-4921</span>
                  </a>
                  <a
                    href="mailto:egarizim@gmail.com"
                    className="flex items-center justify-center sm:justify-start gap-1.5 p-2 sm:p-2.5 rounded-xl bg-slate-950/60 hover:bg-slate-800/60 border border-slate-800/80 text-slate-300 transition-colors min-w-0"
                  >
                    <Mail className="w-3.5 h-3.5 text-amber-400/90 shrink-0" />
                    <span className="truncate">egarizim@gmail.com</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Official Church Contact & Request Form Invitation */}
            <HomeContactCard />
          </div>
        </div>
      </div>

      {/* Interac Donation Modal */}
      <Dialog open={isInteracOpen} onOpenChange={setIsInteracOpen}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-slate-800 text-white p-6 rounded-3xl shadow-2xl">
          <DialogHeader className="text-left space-y-2">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-11 h-11 rounded-2xl bg-amber-500/20 text-amber-400 shrink-0 border border-amber-500/30">
                <Heart className="w-5 h-5 fill-amber-400/20 text-amber-400" />
              </div>
              <div>
                <DialogTitle className="text-lg sm:text-xl font-bold text-white tracking-tight">
                  Don par Virement Interac
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-400 mt-0.5">
                  Église de Dieu Garizim • Dépôt direct sécurisé
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Copy Email Box */}
            <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400 block mb-0.5">
                  Courriel du destinataire
                </span>
                <span className="font-mono text-sm sm:text-base font-bold text-amber-300 select-all truncate block">
                  egarizim@gmail.com
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCopyEmail}
                className="shrink-0 bg-slate-800 hover:bg-slate-700 border-slate-700 text-white font-semibold text-xs px-3.5 h-9 rounded-xl transition-all"
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5 mr-1.5 text-emerald-400" />
                    <span>Copié</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5 mr-1.5 text-slate-300" />
                    <span>Copier</span>
                  </>
                )}
              </Button>
            </div>

            {/* Practical steps */}
            <div className="space-y-2 text-xs text-slate-300 bg-slate-800/40 p-3.5 rounded-2xl border border-slate-800/80">
              <p className="font-semibold text-white flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                Instructions :
              </p>
              <ol className="list-decimal list-inside space-y-1.5 text-slate-300 pl-1 leading-relaxed">
                <li>Accédez à votre compte bancaire en ligne ou mobile.</li>
                <li>
                  Choisissez l'option <strong>Virement Interac</strong>.
                </li>
                <li>
                  Ajoutez le destinataire :{" "}
                  <strong className="text-amber-300 font-mono">
                    egarizim@gmail.com
                  </strong>
                </li>
                <li>
                  Le <strong>dépôt direct</strong> est activé (aucun mot de
                  passe requis).
                </li>
              </ol>
              <p className="text-[11px] text-slate-400 pt-2 border-t border-slate-800 mt-2">
                Pour un reçu d'impôt officiel, veuillez mentionner votre nom
                complet et votre adresse dans le message du virement.
              </p>
            </div>

            {/* Alternative PayPal button */}
            <div className="pt-1 flex items-center justify-between text-xs text-slate-400 border-t border-slate-800/80">
              <span>Autre moyen de donner :</span>
              <a
                href="https://www.paypal.com/donate?hosted_button_id=LPCZP88RDTNK4"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-400 hover:text-amber-300 font-medium inline-flex items-center gap-1 transition-colors"
              >
                <span>Don PayPal</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default HomeGarizim;
